import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { fetchWithBrand } from '../overviewV9/utils/api';
import { Loader2, RefreshCw } from 'lucide-react';

const HIGH5_BRAND_ID = 'b20e13f1-2e3f-4c24-83ce-f2b812bfc3e8';

const tryFormatter = new Intl.NumberFormat('tr-TR', {
  style: 'currency',
  currency: 'TRY',
  maximumFractionDigits: 2,
});

const formatMonth = (monthIso) => {
  // "2026-01-01" -> "2026-01"
  if (!monthIso || typeof monthIso !== 'string') return monthIso || '';
  return monthIso.slice(0, 7);
};

const formatCurrency = (v) => {
  if (v == null || Number.isNaN(Number(v))) return '—';
  return tryFormatter.format(Number(v));
};

const formatPercent = (v) => {
  if (v == null || Number.isNaN(Number(v))) return '—';
  return `${(Number(v) * 100).toFixed(2)}%`;
};

const StatusBadge = ({ status }) => {
  const s = (status || '').toUpperCase();
  const styles = s === 'ENABLED'
    ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
    : s === 'PAUSED'
      ? 'bg-amber-100 text-amber-700 border-amber-200'
      : 'bg-gray-100 text-gray-700 border-gray-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full border ${styles}`}>
      {s || '—'}
    </span>
  );
};

const TypeBadge = ({ type }) => {
  const t = (type || '').toUpperCase();
  return (
    <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full border border-white/20 bg-white/10 text-white">
      {t || '—'}
    </span>
  );
};

export default function High5CampaignMonthlyLive() {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id || (typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const loadData = useCallback(async (opts = {}) => {
    if (!brandId || brandId !== HIGH5_BRAND_ID) return;
    setLoading(true);
    setError(null);
    try {
      const qs = opts.refresh ? '?refresh=1' : '';
      const res = await fetchWithBrand(`/api/google-ads/high5/campaign-monthly-live${qs}`, brandId);
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`API ${res.status}: ${txt.slice(0, 200)}`);
      }
      const json = await res.json();
      if (json && json.success === false) {
        throw new Error(json.error || 'Unknown error from Google Ads API');
      }
      setData(json);
    } catch (e) {
      console.error('[High5CampaignMonthlyLive] load error', e);
      setError(e.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Aggregate totals across all campaigns (YTD)
  const totals = useMemo(() => {
    if (!data?.campaigns) return null;
    let cost = 0;
    let campaignCount = 0;
    let sisSum = 0;
    let sisCount = 0;
    for (const c of data.campaigns) {
      campaignCount += 1;
      for (const m of c.monthly || []) {
        if (typeof m.cost === 'number') cost += m.cost;
        if (m.searchImpressionShare != null) {
          sisSum += m.searchImpressionShare;
          sisCount += 1;
        }
      }
    }
    return {
      cost,
      campaignCount,
      avgSis: sisCount > 0 ? sisSum / sisCount : null,
    };
  }, [data]);

  if (brandId && brandId !== HIGH5_BRAND_ID) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Erişim Kısıtlı</h1>
        <p className="text-gray-600">Bu sayfa yalnızca High5 markası için erişilebilir.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="bg-[#111] p-6 text-white flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-5">
            <div className="bg-white rounded-lg px-4 py-2">
              <img src="/high5logo.svg" alt="High5" className="h-9 w-auto object-contain" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Kampanya Aylık Canlı Metrikler</h1>
              <p className="text-sm text-neutral-400 mt-0.5">
                Google Ads API — gerçek zamanlı · {data?.range?.start || '…'} → {data?.range?.end || '…'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => loadData({ refresh: true })}
              disabled={loading}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-black text-sm font-semibold hover:bg-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Yenile
            </button>
          </div>
        </div>

        {/* Totals strip */}
        {totals && (
          <div className="grid grid-cols-1 sm:grid-cols-3 border-t">
            <div className="p-5 border-b sm:border-b-0 sm:border-r">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Toplam Kampanya</p>
              <p className="text-2xl font-bold text-gray-900">{totals.campaignCount}</p>
            </div>
            <div className="p-5 border-b sm:border-b-0 sm:border-r">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">YTD Toplam Harcama</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(totals.cost)}</p>
            </div>
            <div className="p-5">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Ort. Search IS</p>
              <p className="text-2xl font-bold text-gray-900">{formatPercent(totals.avgSis)}</p>
            </div>
          </div>
        )}
      </div>

      {/* Loading state */}
      {loading && !data && (
        <div className="bg-white rounded-2xl shadow-sm border p-12 flex flex-col items-center justify-center gap-3">
          <Loader2 className="w-8 h-8 text-gray-500 animate-spin" />
          <p className="text-sm text-gray-600">Google Ads'den canlı veri çekiliyor…</p>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
          <p className="text-sm font-semibold text-red-800 mb-1">Veri çekilemedi</p>
          <p className="text-xs text-red-700 font-mono whitespace-pre-wrap">{error}</p>
          <button
            type="button"
            onClick={() => loadData()}
            className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-semibold hover:bg-red-700"
          >
            Tekrar dene
          </button>
        </div>
      )}

      {/* Campaign cards */}
      {data?.campaigns && data.campaigns.length === 0 && !loading && (
        <div className="bg-white rounded-2xl shadow-sm border p-12 text-center text-sm text-gray-600">
          Bu tarih aralığında ENABLED / PAUSED kampanya bulunamadı.
        </div>
      )}

      {data?.campaigns?.map((campaign) => (
        <div key={campaign.id} className="bg-white rounded-2xl shadow-sm border overflow-hidden">
          <div className="bg-[#111] px-6 py-4 text-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-base font-bold tracking-tight">{campaign.name}</h2>
              <p className="text-[11px] text-neutral-400 mt-0.5">ID: {campaign.id}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <StatusBadge status={campaign.status} />
              <TypeBadge type={campaign.type} />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-6 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Ay</th>
                  <th className="text-right px-6 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Cost</th>
                  <th className="text-right px-6 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">CPC</th>
                  <th className="text-right px-6 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Search Impression Share</th>
                </tr>
              </thead>
              <tbody>
                {(campaign.monthly || []).length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-6 text-center text-xs text-gray-500">Bu tarih aralığında veri yok.</td>
                  </tr>
                ) : (
                  campaign.monthly.map((row) => (
                    <tr key={row.month} className="border-b last:border-b-0 hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-3 font-mono text-gray-900">{formatMonth(row.month)}</td>
                      <td className="px-6 py-3 text-right font-semibold text-gray-900 tabular-nums">{formatCurrency(row.cost)}</td>
                      <td className="px-6 py-3 text-right text-gray-700 tabular-nums">{formatCurrency(row.cpc)}</td>
                      <td className="px-6 py-3 text-right text-gray-700 tabular-nums">{formatPercent(row.searchImpressionShare)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
